import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Brain, CheckCircle, TrendingUp, AlertCircle, ChevronRight, Search, Filter, Calendar, Mail, Download, UserPlus, Send, X, Eye, EyeOff, Radio, MessageSquare, HelpCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { isUserAdmin } from '../../lib/auth';
import { getSIAdminCoaches, getSIAdminStats, type SICoachRow, type SIAdminStats } from '../../lib/si-admin';
import { supabase } from '../../lib/supabase';

type FilterMode = 'all' | 'active' | 'never_used';
type AdminTab = 'coaches' | 'users' | 'walkthrough' | 'live' | 'enquiries';

type Enquiry = {
  id: string;
  name: string;
  email: string;
  message: string;
  topic: string | null;
  source_page: string;
  status: 'new' | 'replied' | 'follow_up' | 'closed';
  created_at: string;
};

type WalkthroughRequest = {
  id: string;
  name: string;
  email: string;
  created_at: string;
};

type RegisteredUser = {
  id: string;
  full_name: string;
  email: string;
  created_at: string;
  si_unlocked: boolean;
  app_source: string;
  session_count: number;
};

function StatusBadge({ coach }: { coach: SICoachRow }) {
  if (!coach.is_active) {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-10 text-gray-50">Inactive</span>;
  }
  if (coach.session_count === 0) {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-600">Registered</span>;
  }
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-10 text-success-50">Active</span>;
}

function TierBadge({ tier }: { tier: string }) {
  const map: Record<string, string> = {
    basic: 'bg-gray-10 text-gray-50',
    basic_plus: 'bg-blue-50 text-blue-600',
    advanced: 'bg-purple-50 text-purple-600',
    partner: 'bg-success-10 text-success-50',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${map[tier] ?? 'bg-gray-10 text-gray-50'}`}>
      {tier.replace('_', ' ')}
    </span>
  );
}

export function SIAdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(true);
  const [coaches, setCoaches] = useState<SICoachRow[]>([]);
  const [stats, setStats] = useState<SIAdminStats | null>(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterMode>('all');
  const [sortBy, setSortBy] = useState<'joined' | 'sessions' | 'name'>('joined');
  const [walkthroughRequests, setWalkthroughRequests] = useState<WalkthroughRequest[]>([]);
  const [registeredUsers, setRegisteredUsers] = useState<RegisteredUser[]>([]);
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [activeTab, setActiveTab] = useState<AdminTab>(() => {
    const t = searchParams.get('tab');
    return (t === 'live' || t === 'users' || t === 'walkthrough' || t === 'enquiries') ? t : 'coaches';
  });
  const [userSearch, setUserSearch] = useState('');
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resendResult, setResendResult] = useState<Record<string, 'sent' | 'error'>>({});
  const [quickFixSendingId, setQuickFixSendingId] = useState<string | null>(null);
  const [quickFixResult, setQuickFixResult] = useState<Record<string, 'sent' | 'error'>>({});
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ sent: 0, total: 0 });
  const [addCoachOpen, setAddCoachOpen] = useState(false);
  const [addCoachForm, setAddCoachForm] = useState({ fullName: '', email: '', password: '', sendWelcome: true });
  const [isActivateFlow, setIsActivateFlow] = useState(false);
  const [addCoachSubmitting, setAddCoachSubmitting] = useState(false);
  const [addCoachError, setAddCoachError] = useState('');
  const [addCoachSuccess, setAddCoachSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activateResults, setActivateResults] = useState<Record<string, { userId: string }>>({});
  const [resendingActivation, setResendingActivation] = useState<string | null>(null);
  const [resendActivationResult, setResendActivationResult] = useState<Record<string, 'sent' | 'error'>>({});
  const [togglingUnlockId, setTogglingUnlockId] = useState<string | null>(null);
  const [unlockingAndEmailingId, setUnlockingAndEmailingId] = useState<string | null>(null);
  const [unlockEmailResult, setUnlockEmailResult] = useState<Record<string, 'done' | 'error'>>({});

  // Live stream state
  const [liveStreamId, setLiveStreamId] = useState<string | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [streamTitle, setStreamTitle] = useState('Session Intelligence™ Live Platform Walkthrough');
  const [isLive, setIsLive] = useState(false);
  const [liveViewers, setLiveViewers] = useState<{ id: string; name: string; joined_at: string }[]>([]);
  const [liveMessages, setLiveMessages] = useState<{ id: string; name: string; message: string; created_at: string }[]>([]);
  const [savingStream, setSavingStream] = useState(false);
  const [saveStreamMsg, setSaveStreamMsg] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderResult, setReminderResult] = useState<{ sent: number; failed: number } | null>(null);

  useEffect(() => {
    if (!user) { navigate('/si/login', { replace: true }); return; }
    isUserAdmin(user.id).then(admin => {
      if (!admin) { navigate('/si/dashboard', { replace: true }); return; }
      setChecking(false);
      getSIAdminCoaches()
        .then(rows => {
          setCoaches(rows);
          return getSIAdminStats(rows);
        })
        .then(setStats)
        .catch(err => setError(err.message || 'Failed to load data'));
      supabase
        .from('si_walkthrough_requests' as never)
        .select('*')
        .order('created_at', { ascending: false })
        .then(({ data }) => { if (data) setWalkthroughRequests(data as unknown as WalkthroughRequest[]); });

      supabase
        .from('demo_enquiries' as never)
        .select('*')
        .order('created_at', { ascending: false })
        .then(({ data }) => { if (data) setEnquiries(data as unknown as Enquiry[]); });

      // Load live stream settings
      supabase
        .from('live_stream_settings' as never)
        .select('*')
        .limit(1)
        .maybeSingle()
        .then(({ data }: { data: any }) => {
          if (data) {
            setLiveStreamId(data.id);
            setYoutubeUrl(data.youtube_url ?? '');
            setStreamTitle(data.stream_title ?? '');
            setIsLive(data.is_live ?? false);
          }
        });

      // Load live viewers
      supabase
        .from('live_viewers' as never)
        .select('*')
        .order('joined_at', { ascending: false })
        .then(({ data }) => { if (data) setLiveViewers(data as any[]); });

      // Load live chat messages
      supabase
        .from('live_chat_messages' as never)
        .select('*')
        .order('created_at', { ascending: false })
        .then(({ data }) => { if (data) setLiveMessages(data as any[]); });

      // Load registered users (all coaches with app_source = 'si')
      supabase
        .from('coaches')
        .select(`
          id,
          created_at,
          si_unlocked,
          app_source,
          users!coaches_user_id_fkey ( full_name, email ),
          coaching_clients ( coaching_sessions ( id ) )
        `)
        .order('created_at', { ascending: false })
        .then(({ data }) => {
          if (!data) return;
          const mapped: RegisteredUser[] = (data as any[]).map(c => ({
            id: c.id,
            full_name: c.users?.full_name ?? 'Unknown',
            email: c.users?.email ?? '',
            created_at: c.created_at,
            si_unlocked: c.si_unlocked ?? false,
            app_source: c.app_source ?? 'hpos',
            session_count: (c.coaching_clients || []).reduce(
              (sum: number, cl: any) => sum + (cl.coaching_sessions?.length ?? 0), 0
            ),
          }));
          setRegisteredUsers(mapped);
        });
    });
  }, [user, navigate]);

  if (checking) {
    return (
      <div className="min-h-screen bg-brand-5 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-success-40 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const filtered = coaches
    .filter(c => {
      if (filter === 'active') return c.session_count > 0 && c.is_active;
      if (filter === 'never_used') return c.session_count === 0;
      return true;
    })
    .filter(c => {
      if (!search) return true;
      const q = search.toLowerCase();
      return c.full_name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sortBy === 'sessions') return b.session_count - a.session_count;
      if (sortBy === 'name') return a.full_name.localeCompare(b.full_name);
      return new Date(b.joined_at).getTime() - new Date(a.joined_at).getTime();
    });

  const filteredUsers = registeredUsers.filter(u => {
    if (!userSearch) return true;
    const q = userSearch.toLowerCase();
    return u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  const resendWelcomeEmail = async (u: RegisteredUser) => {
    setResendingId(u.id);
    try {
      // Look up the auth user id from users table (coach row id != auth id)
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('email', u.email)
        .single();

      if (!userData?.id) throw new Error('User not found');

      const { error } = await supabase.functions.invoke('send-email', {
        body: {
          type: 'resend-si-welcome',
          recipientEmail: u.email,
          recipientName: u.full_name,
          userId: userData.id,
          loginUrl: 'https://sessionintelligence.app/si/login',
        },
      });
      if (error) throw error;
      setResendResult(prev => ({ ...prev, [u.id]: 'sent' }));
    } catch {
      setResendResult(prev => ({ ...prev, [u.id]: 'error' }));
    } finally {
      setResendingId(null);
    }
  };

  const sendQuickFixEmail = async (id: string, name: string, email: string) => {
    try {
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('email', email)
        .maybeSingle();

      const body: Record<string, unknown> = {
        type: 'si-quick-fix',
        recipientEmail: email,
        recipientName: name,
        loginUrl: 'https://sessionintelligence.app/si/login',
      };
      if ((userData as { id?: string } | null)?.id) body.userId = (userData as { id: string }).id;

      const { error } = await supabase.functions.invoke('send-email', { body });
      if (error) throw error;
      return true;
    } catch {
      return false;
    }
  };

  const handleSendQuickFix = async (id: string, name: string, email: string) => {
    setQuickFixSendingId(id);
    const success = await sendQuickFixEmail(id, name, email);
    setQuickFixResult(prev => ({ ...prev, [id]: success ? 'sent' : 'error' }));
    setQuickFixSendingId(null);
  };

  const handleSendAllQuickFix = async () => {
    if (!window.confirm(`Send the Quick Fix email to all ${walkthroughRequests.length} walkthrough registrants?`)) return;
    setBulkSending(true);
    setBulkProgress({ sent: 0, total: walkthroughRequests.length });
    let sent = 0;
    for (const req of walkthroughRequests) {
      const success = await sendQuickFixEmail(req.id, req.name, req.email);
      setQuickFixResult(prev => ({ ...prev, [req.id]: success ? 'sent' : 'error' }));
      sent++;
      setBulkProgress({ sent, total: walkthroughRequests.length });
      if (sent < walkthroughRequests.length) await new Promise(r => setTimeout(r, 300));
    }
    setBulkSending(false);
  };

  const handleSendAllQuickFixUsers = async () => {
    if (!window.confirm(`Send the Quick Fix email to all ${filteredUsers.length} registered users?`)) return;
    setBulkSending(true);
    setBulkProgress({ sent: 0, total: filteredUsers.length });
    let sent = 0;
    for (const u of filteredUsers) {
      const success = await sendQuickFixEmail(u.id, u.full_name, u.email);
      setQuickFixResult(prev => ({ ...prev, [u.id]: success ? 'sent' : 'error' }));
      sent++;
      setBulkProgress({ sent, total: filteredUsers.length });
      if (sent < filteredUsers.length) await new Promise(r => setTimeout(r, 300));
    }
    setBulkSending(false);
  };

  const handleSaveStreamSettings = async () => {
    setSavingStream(true);
    setSaveStreamMsg('');
    try {
      if (liveStreamId) {
        await supabase
          .from('live_stream_settings' as never)
          .update({ youtube_url: youtubeUrl, stream_title: streamTitle, is_live: isLive, updated_at: new Date().toISOString() } as never)
          .eq('id' as never, liveStreamId);
      } else {
        const { data } = await supabase
          .from('live_stream_settings' as never)
          .insert({ youtube_url: youtubeUrl, stream_title: streamTitle, is_live: isLive } as never)
          .select()
          .maybeSingle() as { data: any };
        if (data) setLiveStreamId(data.id);
      }
      setSaveStreamMsg('Saved!');
      setTimeout(() => setSaveStreamMsg(''), 3000);
    } catch {
      setSaveStreamMsg('Error saving');
    } finally {
      setSavingStream(false);
    }
  };

  const handleSendReminder = async () => {
    if (!window.confirm(`Send a "Live Now" reminder email to all registered SI coaches? This will send to everyone with app_source = 'si'.`)) return;
    setSendingReminder(true);
    setReminderResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-walkthrough-reminder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
      });
      const json = await res.json();
      if (json.sent !== undefined) {
        setReminderResult({ sent: json.sent, failed: json.failed ?? 0 });
      } else {
        alert('Error: ' + (json.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Failed to send: ' + (err as Error).message);
    } finally {
      setSendingReminder(false);
    }
  };

  const handleToggleUnlock = async (u: RegisteredUser) => {
    const newValue = !u.si_unlocked;
    const action = newValue ? 'unlock' : 'lock';
    if (!window.confirm(`${newValue ? 'Unlock' : 'Revoke'} access for ${u.full_name}? This will ${action} their Session Intelligence account.`)) return;
    setTogglingUnlockId(u.id);
    try {
      const { error } = await supabase
        .from('coaches')
        .update({ si_unlocked: newValue })
        .eq('id', u.id);
      if (error) throw error;
      setRegisteredUsers(prev => prev.map(r => r.id === u.id ? { ...r, si_unlocked: newValue } : r));
    } catch (err) {
      alert('Failed to update: ' + (err as Error).message);
    } finally {
      setTogglingUnlockId(null);
    }
  };

  const handleUnlockAndEmail = async (u: RegisteredUser) => {
    setUnlockingAndEmailingId(u.id);
    try {
      const { error: unlockError } = await supabase
        .from('coaches')
        .update({ si_unlocked: true })
        .eq('id', u.id);
      if (unlockError) throw unlockError;
      setRegisteredUsers(prev => prev.map(r => r.id === u.id ? { ...r, si_unlocked: true } : r));

      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('email', u.email)
        .single();

      if (userData?.id) {
        await supabase.functions.invoke('send-email', {
          body: {
            type: 'resend-si-welcome',
            recipientEmail: u.email,
            recipientName: u.full_name,
            userId: userData.id,
            loginUrl: 'https://sessionintelligence.app/si/login',
          },
        });
      }
      setUnlockEmailResult(prev => ({ ...prev, [u.id]: 'done' }));
    } catch (err) {
      setUnlockEmailResult(prev => ({ ...prev, [u.id]: 'error' }));
      alert('Failed to unlock: ' + (err as Error).message);
    } finally {
      setUnlockingAndEmailingId(null);
    }
  };

  const handleAddCoach = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddCoachError('');
    setAddCoachSuccess('');
    setAddCoachSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-si-coach', {
        body: {
          fullName: addCoachForm.fullName.trim(),
          email: addCoachForm.email.trim().toLowerCase(),
          password: addCoachForm.password,
          sendWelcomeEmail: addCoachForm.sendWelcome,
        },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      setAddCoachSuccess(
        `Coach created successfully${data?.emailSent ? ' — activation email sent.' : '.'}`
      );
      if (data?.userId) {
        setActivateResults(prev => ({ ...prev, [addCoachForm.email]: { userId: data.userId } }));
      }
      setAddCoachForm({ fullName: '', email: '', password: '', sendWelcome: true });
      // Refresh the registered users list
      supabase
        .from('coaches')
        .select(`id, created_at, si_unlocked, app_source, users!coaches_user_id_fkey ( full_name, email ), coaching_clients ( coaching_sessions ( id ) )`)
        .order('created_at', { ascending: false })
        .then(({ data: coachData }) => {
          if (!coachData) return;
          const mapped: RegisteredUser[] = (coachData as any[]).map(c => ({
            id: c.id,
            full_name: c.users?.full_name ?? 'Unknown',
            email: c.users?.email ?? '',
            created_at: c.created_at,
            si_unlocked: c.si_unlocked ?? false,
            app_source: c.app_source ?? 'hpos',
            session_count: (c.coaching_clients || []).reduce(
              (sum: number, cl: any) => sum + (cl.coaching_sessions?.length ?? 0), 0
            ),
          }));
          setRegisteredUsers(mapped);
        });
    } catch (err) {
      setAddCoachError((err as Error).message || 'Failed to create coach');
    } finally {
      setAddCoachSubmitting(false);
    }
  };

  const openActivateModal = (name: string, email: string) => {
    setIsActivateFlow(true);
    setAddCoachForm({ fullName: name, email, password: '', sendWelcome: true });
    setAddCoachError('');
    setAddCoachSuccess('');
    setAddCoachOpen(true);
  };

  const handleResendActivation = async (email: string, name: string) => {
    const entry = activateResults[email];
    if (!entry?.userId) return;
    setResendingActivation(email);
    try {
      const { error } = await supabase.functions.invoke('send-email', {
        body: {
          type: 'resend-si-welcome',
          recipientEmail: email,
          recipientName: name,
          userId: entry.userId,
          loginUrl: 'https://sessionintelligence.app/si/login',
        },
      });
      if (error) throw error;
      setResendActivationResult(prev => ({ ...prev, [email]: 'sent' }));
      setTimeout(() => setResendActivationResult(prev => { const n = { ...prev }; delete n[email]; return n; }), 4000);
    } catch {
      setResendActivationResult(prev => ({ ...prev, [email]: 'error' }));
    } finally {
      setResendingActivation(null);
    }
  };

  const exportUsersCSV = () => {
    const rows = [
      ['Name', 'Email', 'Registered', 'Unlocked', 'Sessions', 'Source'],
      ...filteredUsers.map(u => [
        u.full_name,
        u.email,
        new Date(u.created_at).toLocaleDateString('en-GB'),
        u.si_unlocked ? 'Yes' : 'No',
        String(u.session_count),
        u.app_source,
      ]),
    ];
    const csv = rows.map(r => r.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `si-users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activationRate = stats && stats.total_coaches > 0
    ? Math.round((stats.active_coaches / stats.total_coaches) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-brand-5">
      <div className="section-container py-8 space-y-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-80">Session Intelligence<sup className="text-sm font-normal">™</sup> Admin</h1>
            <p className="text-sm text-gray-50 mt-1">Monitor practitioner usage and engagement</p>
          </div>
          <button
            onClick={() => { setIsActivateFlow(false); setAddCoachOpen(true); setAddCoachError(''); setAddCoachSuccess(''); }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-success-50 hover:bg-success-60 text-white rounded-xl text-sm font-semibold transition-colors flex-shrink-0"
          >
            <UserPlus className="w-4 h-4" />
            Add Coach
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-10 rounded-2xl p-1 w-fit">
          {([
            { id: 'coaches', label: 'Active Coaches', icon: Brain },
            { id: 'users', label: `All Users (${registeredUsers.length})`, icon: UserPlus },
            { id: 'walkthrough', label: `Walkthrough Requests (${walkthroughRequests.length})`, icon: Calendar },
            { id: 'enquiries', label: `Enquiries (${enquiries.length})`, icon: HelpCircle },
            { id: 'live', label: 'Live Stream', icon: Radio },
          ] as { id: AdminTab; label: string; icon: React.ElementType }[]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                activeTab === tab.id ? 'bg-white text-gray-80 shadow-soft' : 'text-gray-50 hover:text-gray-80'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 text-red-700 p-4 rounded-2xl text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* ── COACHES TAB ── */}
        {activeTab === 'coaches' && (
          <>
            {/* Stats cards */}
            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {[
                  { label: 'Total Coaches', value: stats.total_coaches, icon: Users, color: 'text-gray-80', bg: 'bg-white' },
                  { label: 'Active', value: stats.active_coaches, icon: CheckCircle, color: 'text-success-50', bg: 'bg-white' },
                  { label: 'Never Used', value: stats.never_used, icon: AlertCircle, color: 'text-orange-500', bg: 'bg-white' },
                  { label: 'Total Sessions', value: stats.total_sessions, icon: Brain, color: 'text-blue-600', bg: 'bg-white' },
                  { label: 'AI Analyses', value: stats.total_analyses, icon: TrendingUp, color: 'text-success-50', bg: 'bg-white' },
                ].map((stat, i) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className={`${stat.bg} rounded-2xl border border-gray-20 p-4`}
                  >
                    <stat.icon className={`w-5 h-5 ${stat.color} mb-2`} />
                    <p className="text-2xl font-bold text-gray-80">{stat.value}</p>
                    <p className="text-xs text-gray-50 mt-0.5">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Activation rate bar */}
            {stats && stats.total_coaches > 0 && (
              <div className="bg-white rounded-2xl border border-gray-20 p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-80">Activation Rate</span>
                  <span className="text-sm font-bold text-success-50">{activationRate}%</span>
                </div>
                <div className="h-2.5 bg-gray-10 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${activationRate}%` }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="h-full bg-success-40 rounded-full"
                  />
                </div>
                <p className="text-xs text-gray-50 mt-2">
                  {stats.active_coaches} of {stats.total_coaches} coaches have run at least one session
                </p>
              </div>
            )}

            {/* Coaches table */}
            <div className="bg-white rounded-2xl border border-gray-20 overflow-hidden">
              <div className="p-4 border-b border-gray-10 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-40" />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search coaches..."
                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-20 rounded-xl bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-40" />
                  <div className="flex rounded-xl border border-gray-20 overflow-hidden text-xs">
                    {(['all', 'active', 'never_used'] as FilterMode[]).map(f => (
                      <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-3 py-1.5 font-medium transition-colors ${filter === f ? 'bg-gray-80 text-white' : 'text-gray-50 hover:bg-brand-5'}`}
                      >
                        {f === 'never_used' ? 'Never Used' : f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                  </div>
                  <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value as 'joined' | 'sessions' | 'name')}
                    className="text-xs border border-gray-20 rounded-xl px-2 py-1.5 bg-brand-5 text-gray-60 focus:outline-none focus:border-success-40"
                  >
                    <option value="joined">Newest first</option>
                    <option value="sessions">Most sessions</option>
                    <option value="name">Name A–Z</option>
                  </select>
                </div>
              </div>

              {filtered.length === 0 ? (
                <div className="p-12 text-center text-sm text-gray-50">
                  No coaches match the current filter.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-brand-5 text-left">
                        <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Coach</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Tier</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Status</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide text-right">Clients</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide text-right">Sessions</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide text-right">Analyses</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Last Session</th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Joined</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-10">
                      {filtered.map(coach => (
                        <tr key={coach.coach_id} className="hover:bg-brand-5 transition-colors group">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-gray-80">{coach.full_name}</p>
                              <p className="text-xs text-gray-40">{coach.email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3"><TierBadge tier={coach.tier} /></td>
                          <td className="px-4 py-3"><StatusBadge coach={coach} /></td>
                          <td className="px-4 py-3 text-right font-medium text-gray-80">{coach.client_count}</td>
                          <td className="px-4 py-3 text-right font-medium text-gray-80">{coach.session_count}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`font-medium ${coach.analyses_completed > 0 ? 'text-success-50' : 'text-gray-40'}`}>
                              {coach.analyses_completed}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-50">
                            {coach.last_session_date
                              ? new Date(coach.last_session_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                              : <span className="text-gray-30">—</span>}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-50">
                            {new Date(coach.joined_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="px-4 py-3">
                            <Link
                              to={`/si/admin/coach/${coach.coach_id}`}
                              className="inline-flex items-center gap-1 text-xs text-success-50 font-medium opacity-0 group-hover:opacity-100 transition-opacity hover:underline"
                            >
                              View <ChevronRight className="w-3 h-3" />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── ALL USERS TAB ── */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-2xl border border-gray-20 overflow-hidden">
            <div className="p-4 border-b border-gray-10 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-40" />
                <input
                  type="text"
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-20 rounded-xl bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors"
                />
              </div>
              <button
                onClick={exportUsersCSV}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-80 hover:bg-gray-60 text-white rounded-xl text-xs font-semibold transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Export CSV
              </button>
              <button
                onClick={handleSendAllQuickFixUsers}
                disabled={bulkSending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold transition-colors disabled:opacity-50"
              >
                <Send className="w-3.5 h-3.5" />
                {bulkSending
                  ? `Sending ${bulkProgress.sent}/${bulkProgress.total}…`
                  : 'Send Quick Fix to All'}
              </button>
            </div>
            {filteredUsers.length === 0 ? (
              <div className="p-12 text-center text-sm text-gray-50">No registered users found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-brand-5 text-left">
                      <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Name</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Email</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Registered</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Source</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide text-right">Sessions</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Unlocked</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-10">
                    {filteredUsers.map((u, i) => (
                      <motion.tr
                        key={u.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.02 }}
                        className="hover:bg-brand-5 transition-colors"
                      >
                        <td className="px-4 py-3 font-medium text-gray-80">{u.full_name}</td>
                        <td className="px-4 py-3 text-gray-50">
                          <a href={`mailto:${u.email}`} className="flex items-center gap-1.5 hover:text-success-50 transition-colors">
                            <Mail className="w-3.5 h-3.5" />
                            {u.email}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-50">
                          {new Date(u.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${u.app_source === 'si' ? 'bg-success-10 text-success-50' : 'bg-gray-10 text-gray-50'}`}>
                            {u.app_source === 'si' ? 'Session Intelligence' : 'HPOS'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-80">{u.session_count}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleToggleUnlock(u)}
                            disabled={togglingUnlockId === u.id}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors disabled:opacity-50 ${
                              u.si_unlocked
                                ? 'bg-success-10 text-success-50 hover:bg-red-50 hover:text-red-600'
                                : 'bg-orange-10 text-orange-70 hover:bg-success-10 hover:text-success-50'
                            }`}
                            title={u.si_unlocked ? 'Click to revoke access' : 'Click to unlock access'}
                          >
                            {togglingUnlockId === u.id
                              ? <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                              : u.si_unlocked
                                ? <><Eye className="w-3 h-3" />Unlocked</>
                                : <><EyeOff className="w-3 h-3" />Pending</>
                            }
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {resendResult[u.id] === 'sent' ? (
                              <span className="text-xs text-success-50 font-medium">Welcome sent!</span>
                            ) : resendResult[u.id] === 'error' ? (
                              <span className="text-xs text-red-500 font-medium">Failed</span>
                            ) : (
                              <button
                                onClick={() => resendWelcomeEmail(u)}
                                disabled={resendingId === u.id || bulkSending}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl bg-success-10 text-success-50 hover:bg-success-20 transition-colors disabled:opacity-50"
                              >
                                <Send className="w-3 h-3" />
                                {resendingId === u.id ? 'Sending…' : 'Resend Welcome'}
                              </button>
                            )}
                            {quickFixResult[u.id] === 'sent' ? (
                              <span className="text-xs text-blue-600 font-medium">Quick Fix sent!</span>
                            ) : quickFixResult[u.id] === 'error' ? (
                              <span className="text-xs text-red-500 font-medium">QF failed</span>
                            ) : (
                              <button
                                onClick={() => handleSendQuickFix(u.id, u.full_name, u.email)}
                                disabled={quickFixSendingId === u.id || bulkSending}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors disabled:opacity-50"
                              >
                                <Send className="w-3 h-3" />
                                {quickFixSendingId === u.id ? 'Sending…' : 'Send Quick Fix'}
                              </button>
                            )}
                            {!u.si_unlocked && (
                              unlockEmailResult[u.id] === 'done' ? (
                                <span className="text-xs text-success-50 font-medium flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />Unlocked & emailed
                                </span>
                              ) : unlockEmailResult[u.id] === 'error' ? (
                                <span className="text-xs text-red-500 font-medium">Failed</span>
                              ) : (
                                <button
                                  onClick={() => handleUnlockAndEmail(u)}
                                  disabled={unlockingAndEmailingId === u.id}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl bg-success-50 text-white hover:bg-success-60 transition-colors disabled:opacity-50"
                                >
                                  {unlockingAndEmailingId === u.id ? (
                                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <UserPlus className="w-3 h-3" />
                                  )}
                                  {unlockingAndEmailingId === u.id ? 'Activating…' : 'Unlock & Send Email'}
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── WALKTHROUGH REQUESTS TAB ── */}
        {activeTab === 'walkthrough' && (
          <div className="bg-white rounded-2xl border border-gray-20 overflow-hidden">
            <div className="p-5 border-b border-gray-10 flex items-center gap-3">
              <Calendar className="w-5 h-5 text-success-50" />
              <div>
                <h2 className="text-sm font-semibold text-gray-80">Walkthrough Requests</h2>
                <p className="text-xs text-gray-50">People who registered for the live walkthrough</p>
              </div>
              <span className="ml-auto inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-success-10 text-success-60">
                {walkthroughRequests.length}
              </span>
              {walkthroughRequests.length > 0 && (
                <button
                  onClick={handleSendAllQuickFix}
                  disabled={bulkSending}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-80 hover:bg-gray-60 text-white rounded-xl text-xs font-semibold transition-colors disabled:opacity-50"
                >
                  <Send className="w-3.5 h-3.5" />
                  {bulkSending
                    ? `Sending ${bulkProgress.sent}/${bulkProgress.total}…`
                    : 'Send Quick Fix to All'}
                </button>
              )}
            </div>
            {walkthroughRequests.length === 0 ? (
              <div className="p-12 text-center text-sm text-gray-50">No walkthrough requests yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-brand-5 text-left">
                      <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Name</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Email</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Registered</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-10">
                    {walkthroughRequests.map((req, i) => (
                      <motion.tr
                        key={req.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.03 }}
                        className="hover:bg-brand-5 transition-colors"
                      >
                        <td className="px-4 py-3 font-medium text-gray-80">{req.name}</td>
                        <td className="px-4 py-3 text-gray-50">
                          <a href={`mailto:${req.email}`} className="flex items-center gap-1.5 hover:text-success-50 transition-colors">
                            <Mail className="w-3.5 h-3.5" />
                            {req.email}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-50">
                          {new Date(req.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                          {quickFixResult[req.id] === 'sent' ? (
                            <span className="text-xs text-success-50 font-medium">Sent!</span>
                          ) : quickFixResult[req.id] === 'error' ? (
                            <span className="text-xs text-red-500 font-medium">Failed</span>
                          ) : (
                            <button
                              onClick={() => handleSendQuickFix(req.id, req.name, req.email)}
                              disabled={quickFixSendingId === req.id || bulkSending}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors disabled:opacity-50"
                            >
                              <Send className="w-3 h-3" />
                              {quickFixSendingId === req.id ? 'Sending…' : 'Send Quick Fix'}
                            </button>
                          )}
                          {activateResults[req.email] ? (
                            <span className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-xs text-success-50 font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" />Activated</span>
                              {resendActivationResult[req.email] === 'sent' ? (
                                <span className="text-xs text-success-50 font-medium">Email sent!</span>
                              ) : resendActivationResult[req.email] === 'error' ? (
                                <span className="text-xs text-red-500 font-medium">Failed</span>
                              ) : (
                                <button
                                  onClick={() => handleResendActivation(req.email, req.name)}
                                  disabled={resendingActivation === req.email}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl bg-success-10 text-success-50 hover:bg-success-20 transition-colors disabled:opacity-50"
                                >
                                  <Send className="w-3 h-3" />
                                  {resendingActivation === req.email ? 'Sending…' : 'Resend'}
                                </button>
                              )}
                            </span>
                          ) : (
                            <button
                              onClick={() => openActivateModal(req.name, req.email)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl bg-success-50 text-white hover:bg-success-60 transition-colors"
                            >
                              <UserPlus className="w-3 h-3" />
                              Activate
                            </button>
                          )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── ENQUIRIES TAB ── */}
        {activeTab === 'enquiries' && (
          <div className="bg-white rounded-2xl border border-gray-20 overflow-hidden">
            <div className="p-5 border-b border-gray-10 flex items-center gap-3">
              <HelpCircle className="w-5 h-5 text-gray-50" />
              <div>
                <h2 className="text-sm font-semibold text-gray-80">Demo Questions &amp; Enquiries</h2>
                <p className="text-xs text-gray-50">Questions and account activation requests from the walkthrough replay page</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {enquiries.filter(e => e.status === 'new' && e.topic === 'Account Activation').length > 0 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-success-10 text-success-60">
                    {enquiries.filter(e => e.status === 'new' && e.topic === 'Account Activation').length} Activation{enquiries.filter(e => e.status === 'new' && e.topic === 'Account Activation').length > 1 ? 's' : ''}
                  </span>
                )}
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-600">
                  {enquiries.filter(e => e.status === 'new').length} New
                </span>
              </div>
            </div>

            {enquiries.length === 0 ? (
              <div className="p-10 text-center text-sm text-gray-40">No enquiries yet. Questions from the replay page will appear here.</div>
            ) : (
              <div className="divide-y divide-gray-10">
                {enquiries.map(enq => (
                  <div key={enq.id} className="p-5 flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3 mb-2">
                        <div>
                          <p className="font-semibold text-gray-80 text-sm">{enq.name}</p>
                          <p className="text-xs text-gray-50">{enq.email}</p>
                        </div>
                        {enq.topic && (
                          <span className={`flex-shrink-0 text-xs px-2.5 py-0.5 rounded-full font-semibold ${
                            enq.topic === 'Account Activation'
                              ? 'bg-success-10 text-success-60'
                              : 'bg-[#e8f0dc] text-[#4a7c2f]'
                          }`}>
                            {enq.topic}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-70 leading-relaxed">{enq.message}</p>
                      <p className="text-xs text-gray-40 mt-2">
                        {enq.source_page} &middot; {new Date(enq.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div className="flex items-start gap-2 flex-shrink-0">
                      <select
                        value={enq.status}
                        onChange={async e => {
                          const newStatus = e.target.value;
                          await supabase
                            .from('demo_enquiries' as never)
                            .update({ status: newStatus, updated_at: new Date().toISOString() } as never)
                            .eq('id' as never, enq.id);
                          setEnquiries(prev => prev.map(q => q.id === enq.id ? { ...q, status: newStatus as Enquiry['status'] } : q));
                        }}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-xl border appearance-none cursor-pointer focus:outline-none ${
                          enq.status === 'new' ? 'bg-blue-50 text-blue-600 border-blue-200' :
                          enq.status === 'replied' ? 'bg-success-10 text-success-60 border-success-20' :
                          enq.status === 'follow_up' ? 'bg-amber-50 text-amber-600 border-amber-200' :
                          'bg-gray-10 text-gray-40 border-gray-20'
                        }`}
                      >
                        <option value="new">New</option>
                        <option value="replied">Replied</option>
                        <option value="follow_up">Follow-up Needed</option>
                        <option value="closed">Closed</option>
                      </select>
                      {activateResults[enq.email] ? (
                        <span className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs text-success-50 font-semibold flex items-center gap-1"><CheckCircle className="w-3 h-3" />Activated</span>
                          {resendActivationResult[enq.email] === 'sent' ? (
                            <span className="text-xs text-success-50 font-medium">Email sent!</span>
                          ) : resendActivationResult[enq.email] === 'error' ? (
                            <span className="text-xs text-red-500 font-medium">Failed</span>
                          ) : (
                            <button
                              onClick={() => handleResendActivation(enq.email, enq.name)}
                              disabled={resendingActivation === enq.email}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-success-10 hover:bg-success-20 text-success-50 text-xs font-semibold rounded-xl transition-colors disabled:opacity-50"
                            >
                              <Send className="w-3 h-3" />
                              {resendingActivation === enq.email ? 'Sending…' : 'Resend'}
                            </button>
                          )}
                        </span>
                      ) : (
                        <button
                          onClick={() => openActivateModal(enq.name, enq.email)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-success-50 hover:bg-success-60 text-white text-xs font-semibold rounded-xl transition-colors"
                        >
                          <UserPlus className="w-3 h-3" />
                          Activate
                        </button>
                      )}
                      <a
                        href={`mailto:${enq.email}?subject=Re: Your Session Intelligence question&body=Hi ${enq.name.split(' ')[0]},%0D%0A%0D%0A`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-80 hover:bg-gray-70 text-white text-xs font-semibold rounded-xl transition-colors"
                      >
                        <Mail className="w-3 h-3" />
                        Reply
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── LIVE STREAM TAB ── */}
        {activeTab === 'live' && (
          <div className="space-y-5">

            {/* Stream settings card */}
            <div className="bg-white rounded-2xl border border-gray-20 overflow-hidden">
              <div className="p-5 border-b border-gray-10 flex items-center gap-3">
                <Radio className="w-5 h-5 text-red-500" />
                <div>
                  <h2 className="text-sm font-semibold text-gray-80">Live Stream Settings</h2>
                  <p className="text-xs text-gray-50">Control the YouTube embed and go-live status on the /walkthrough page</p>
                </div>
              </div>
              <div className="p-5 space-y-4">
                {/* is_live toggle */}
                <div className="flex items-center justify-between p-4 rounded-2xl border border-gray-20 bg-brand-5">
                  <div>
                    <p className="text-sm font-semibold text-gray-80">Stream Status</p>
                    <p className="text-xs text-gray-50 mt-0.5">When ON, the video embed and Q&amp;A panel appear on the /walkthrough page</p>
                  </div>
                  <button
                    onClick={() => setIsLive(v => !v)}
                    className={`relative inline-flex h-7 w-12 rounded-full transition-colors ${isLive ? 'bg-red-500' : 'bg-gray-30'}`}
                  >
                    <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${isLive ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                {isLive && (
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 border border-red-200 rounded-2xl">
                    <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
                    </span>
                    <p className="text-xs text-red-700 font-semibold">Stream is LIVE — the embed is visible to viewers right now</p>
                  </div>
                )}

                {/* Stream title */}
                <div>
                  <label className="block text-sm font-medium text-gray-80 mb-1.5">Stream title (shown to viewers)</label>
                  <input
                    type="text"
                    value={streamTitle}
                    onChange={e => setStreamTitle(e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm"
                  />
                </div>

                {/* YouTube URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-80 mb-1.5">YouTube embed URL</label>
                  <input
                    type="url"
                    value={youtubeUrl}
                    onChange={e => setYoutubeUrl(e.target.value)}
                    placeholder="https://www.youtube.com/embed/VIDEO_ID?autoplay=1"
                    className="w-full px-4 py-3 rounded-2xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm"
                  />
                  <p className="text-xs text-gray-40 mt-1.5">
                    Go to YouTube Studio → Live → Get link → Copy the embed URL. Format: <code className="bg-gray-10 px-1 rounded">https://www.youtube.com/embed/VIDEO_ID</code>
                  </p>
                </div>

                {youtubeUrl && (
                  <div className="rounded-2xl overflow-hidden aspect-video bg-black shadow-sm">
                    <iframe
                      src={youtubeUrl}
                      title="Preview"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSaveStreamSettings}
                    disabled={savingStream}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-success-50 hover:bg-success-60 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                  >
                    {savingStream ? 'Saving…' : 'Save Settings'}
                  </button>
                  {saveStreamMsg && (
                    <span className={`text-sm font-medium ${saveStreamMsg === 'Saved!' ? 'text-success-50' : 'text-red-500'}`}>
                      {saveStreamMsg}
                    </span>
                  )}
                </div>

                {/* Send reminder email */}
                <div className="border-t border-gray-10 pt-5 mt-2">
                  <p className="text-sm font-semibold text-gray-80 mb-1">Send "Live Now" Reminder Email</p>
                  <p className="text-xs text-gray-50 mb-3">
                    Sends an immediate reminder to all registered SI coaches (everyone with an SI account) letting them know the walkthrough is starting now — 10 AM EST / 3 PM UK.
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleSendReminder}
                      disabled={sendingReminder}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                    >
                      <span className="relative flex h-2 w-2">
                        {!sendingReminder && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />}
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                      </span>
                      {sendingReminder ? 'Sending…' : 'Send Live Reminder to All'}
                    </button>
                    {reminderResult && (
                      <span className="text-sm font-medium text-success-50">
                        Sent to {reminderResult.sent} coaches{reminderResult.failed > 0 ? ` (${reminderResult.failed} failed)` : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Live viewers */}
            <div className="bg-white rounded-2xl border border-gray-20 overflow-hidden">
              <div className="p-5 border-b border-gray-10 flex items-center gap-3">
                <Users className="w-5 h-5 text-success-50" />
                <div>
                  <h2 className="text-sm font-semibold text-gray-80">Live Viewers</h2>
                  <p className="text-xs text-gray-50">People who entered their name on the walkthrough page during a live event</p>
                </div>
                <span className="ml-auto inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-success-10 text-success-60">
                  {liveViewers.length}
                </span>
              </div>
              {liveViewers.length === 0 ? (
                <div className="p-10 text-center text-sm text-gray-40">No live viewers yet. They appear here when someone joins during a live event.</div>
              ) : (
                <div className="divide-y divide-gray-10 max-h-72 overflow-y-auto">
                  {liveViewers.map(v => (
                    <div key={v.id} className="flex items-center gap-3 px-5 py-3.5">
                      <div className="w-8 h-8 rounded-full bg-success-10 text-success-60 flex items-center justify-center text-xs font-bold uppercase flex-shrink-0">
                        {v.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-80">{v.name}</p>
                        <p className="text-xs text-gray-40">{new Date(v.joined_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Live chat messages */}
            <div className="bg-white rounded-2xl border border-gray-20 overflow-hidden">
              <div className="p-5 border-b border-gray-10 flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-blue-500" />
                <div>
                  <h2 className="text-sm font-semibold text-gray-80">Q&amp;A Messages</h2>
                  <p className="text-xs text-gray-50">Questions submitted by viewers during the live stream</p>
                </div>
                <span className="ml-auto inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-600">
                  {liveMessages.length}
                </span>
              </div>
              {liveMessages.length === 0 ? (
                <div className="p-10 text-center text-sm text-gray-40">No messages yet. Viewer questions will appear here during the live stream.</div>
              ) : (
                <div className="divide-y divide-gray-10 max-h-96 overflow-y-auto">
                  {liveMessages.map(msg => (
                    <div key={msg.id} className="flex items-start gap-3 px-5 py-4">
                      <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-bold uppercase flex-shrink-0">
                        {msg.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-gray-80">{msg.name}</span>
                          <span className="text-xs text-gray-40">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p className="text-sm text-gray-60 leading-relaxed">{msg.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        )}
      </div>

      {/* ── ADD COACH MODAL ── */}
      {addCoachOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl w-full max-w-md"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-10">
              <div>
                <h2 className="text-base font-semibold text-gray-80">{isActivateFlow ? 'Activate Account' : 'Add New Coach'}</h2>
                <p className="text-xs text-gray-50 mt-0.5">
                  {isActivateFlow
                    ? 'Creates their Session Intelligence™ account and sends login details automatically'
                    : 'Creates a Session Intelligence™ account and optionally sends login details'}
                </p>
              </div>
              <button
                onClick={() => { setAddCoachOpen(false); setIsActivateFlow(false); setAddCoachError(''); setAddCoachSuccess(''); }}
                className="p-1.5 rounded-lg hover:bg-gray-10 text-gray-40 hover:text-gray-80 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleAddCoach} className="p-6 space-y-4">
              {addCoachError && (
                <div className="flex items-start gap-2 bg-red-50 text-red-700 p-3 rounded-xl text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  {addCoachError}
                </div>
              )}
              {addCoachSuccess && (
                <div className="flex items-start gap-2 bg-success-10 text-success-60 p-3 rounded-xl text-sm">
                  <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  {addCoachSuccess}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-60 mb-1.5">Full Name</label>
                <input
                  type="text"
                  required
                  value={addCoachForm.fullName}
                  onChange={e => setAddCoachForm(f => ({ ...f, fullName: e.target.value }))}
                  placeholder="Jane Smith"
                  className="w-full px-3 py-2.5 text-sm border border-gray-20 rounded-xl bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-60 mb-1.5">Email Address</label>
                <input
                  type="email"
                  required
                  value={addCoachForm.email}
                  onChange={e => setAddCoachForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="jane@example.com"
                  className="w-full px-3 py-2.5 text-sm border border-gray-20 rounded-xl bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-60 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={addCoachForm.password}
                    onChange={e => setAddCoachForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="Min. 8 characters"
                    className="w-full px-3 py-2.5 pr-10 text-sm border border-gray-20 rounded-xl bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-40 hover:text-gray-60"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {isActivateFlow ? (
                <div className="flex items-center gap-2.5 px-3 py-2.5 bg-success-10 border border-success-20 rounded-xl text-xs text-success-60 font-medium">
                  <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  Activation email with login details will be sent automatically
                </div>
              ) : (
                <label className="flex items-center gap-3 cursor-pointer">
                  <div
                    onClick={() => setAddCoachForm(f => ({ ...f, sendWelcome: !f.sendWelcome }))}
                    className={`w-10 h-5.5 rounded-full transition-colors flex-shrink-0 relative ${addCoachForm.sendWelcome ? 'bg-success-50' : 'bg-gray-20'}`}
                    style={{ height: '22px', width: '40px' }}
                  >
                    <span
                      className={`absolute top-0.5 w-4.5 h-4 rounded-full bg-white shadow transition-transform ${addCoachForm.sendWelcome ? 'translate-x-5' : 'translate-x-0.5'}`}
                      style={{ width: '18px', height: '18px' }}
                    />
                  </div>
                  <span className="text-sm text-gray-70">Send welcome email with login details</span>
                </label>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setAddCoachOpen(false); setIsActivateFlow(false); setAddCoachError(''); setAddCoachSuccess(''); }}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-60 bg-gray-10 hover:bg-gray-20 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addCoachSubmitting}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-success-50 hover:bg-success-60 rounded-xl transition-colors disabled:opacity-50"
                >
                  {addCoachSubmitting ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <UserPlus className="w-4 h-4" />
                  )}
                  {addCoachSubmitting ? 'Creating…' : isActivateFlow ? 'Activate & Send Email' : 'Create Coach'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
